import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { api } from "../api";
import { can } from "../utils/rolePermission";
import "./sidebar.css";

const Sidebar = ({ user }) => {
  const role = String(user?.role || "").toLowerCase();
  const isClient = role === "client";
  const isITManager = role === "it manager";
  const [permissions, setPermissions] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [open, setOpen] = useState(false);
  const [messengerOpen, setMessengerOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [hrOpen, setHrOpen] = useState(false);
  const [hiringOpen, setHiringOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [bookKeepingOpen, setBookKeepingOpen] = useState(false);
  const [financeReportsOpen, setFinanceReportsOpen] = useState(false);
  const [gstReportsOpen, setGstReportsOpen] = useState(false);
  const [tdsReportsOpen, setTdsReportsOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesMastersOpen, setSalesMastersOpen] = useState(false);
  const [companyExpenseOpen, setCompanyExpenseOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [architectOpen, setArchitectOpen] = useState(false);
  const [projectManagementOpen, setProjectManagementOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [sentEmailsOpen, setSentEmailsOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPermissions();
  }, [user?.role]);

  const fetchPermissions = async () => {
    try {
      const res = await api.get("/roles/my-permissions", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setPermissions(res.data?.permissions || {});
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setPermissions({});
    }
  };
  useEffect(() => {
    const path = location.pathname;
    const isToolsRoute =
      path === "/tools/notes" || path.startsWith("/tools/notes/");
    const isMessengerRoute =
      path === "/messenger" || path.startsWith("/messenger/");

    const isOperationsRoute =
      path === "/operations-dashboard" ||
      path.startsWith("/projects/") ||
      path.startsWith("/transmittal") ||
      path === "/checklist" ||
      path.startsWith("/construction-report");

    const isHrRoute =
      path === "/timesheet" ||
      path.startsWith("/employee/logs/") ||
      path === "/admin/log-summary" ||
      path === "/leave-application" ||
      path === "/leave-management" ||
      path === "/manage-employees" ||
      path === "/employee-reviews" ||
      path === "/admin-employee-review-detail" ||
      path === "/project-hours-summary" ||
      path === "/role-permissions" ||
      path === "/hr/create-mail-account";

    const isHiringRoute =
      path.startsWith("/hr-dashboard") ||
      path.startsWith("/hr/job-openings") ||
      path.startsWith("/hr/candidates") ||
      path.startsWith("/hr/interviews") ||
      path.startsWith("/hr/offers") ||
      path.startsWith("/hr/joining") ||
      path.startsWith("/hr/closed-hiring") ||
      path.startsWith("/hr/recruitment-reports");

    const isCrmRoute =
      path === "/crm-dashboard" ||
      path === "/leads" ||
      path.startsWith("/leads/") ||
      path === "/proposals" ||
      path.startsWith("/sent-emails/") ||
      path === "/wons-leads" ||
      path === "/lost-leads" ||
      path === "/reports";

    const isFinanceRoute =
      path === "/finance-entry" ||
      path.startsWith("/finance/reports/") ||
      path === "/project-cost-report" ||
      path.startsWith("/company-expenses/") ||
      path.startsWith("/sales/");

    const isSalesRoute = path.startsWith("/sales/");

    const isSalesMasterRoute =
      path === "/sales/services" ||
      path === "/sales/clients" ||
      path === "/sales/banks" ||
      path === "/sales/terms";

    const isCompanyExpenseRoute = path.startsWith("/company-expenses/");

    // On page load / refresh, only the group related to the current page opens.
    setToolsOpen(isToolsRoute);
    setMessengerOpen(isMessengerRoute);
    setOperationsOpen(isOperationsRoute);
    setHrOpen(isHrRoute);
    setHiringOpen(isHiringRoute);
    setCrmOpen(isCrmRoute);
    setFinanceOpen(isFinanceRoute);
    const isReportRoute = path.startsWith("/finance/reports/");
    setBookKeepingOpen(path === "/finance-entry" || isReportRoute);
    setFinanceReportsOpen(isReportRoute);
    setGstReportsOpen(path.startsWith("/finance/reports/gst/"));
    setTdsReportsOpen(path.startsWith("/finance/reports/tds/"));
    setSalesOpen(isSalesRoute);
    setSalesMastersOpen(isSalesMasterRoute);
    setCompanyExpenseOpen(isCompanyExpenseRoute);

    if (path.startsWith("/sent-emails/")) {
      setSentEmailsOpen(true);
    } else {
      setSentEmailsOpen(false);
    }
  }, [location.pathname]);
  const has = (moduleKey, action = "view") =>
    can(user, permissions, moduleKey, action);

  // "Sent Emails" has no generic "view" action in pagePermissions.js — its
  // actions are viewIntro / viewPortfolio / viewProposal. So visibility must
  // check whether ANY of those three is granted, not a "view" action that
  // never actually exists for this module.
  const hasSentEmailsAccess =
    has("sentEmails", "viewIntro") ||
    has("sentEmails", "viewPortfolio") ||
    has("sentEmails", "viewProposal");

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobile(mobile);
      if (!mobile) setOpen(false);
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const handler = () => setOpen((v) => !v);
    window.addEventListener("sidebar:toggle", handler);
    return () => window.removeEventListener("sidebar:toggle", handler);
  }, []);

  const closeMobile = () => {
    if (isMobile) setOpen(false);
  };
  const showTools = has("notes", "view");

  const showMessenger =
    has("messengerUser", "view") ||
    has("messengerProject", "view") ||
    has("messengerCategory", "view");

  // Mail permissions (Role & Permission page > Mail Permissions)
  const showEmail = has("email", "view");
  const showCreateMailAccount = has("createMailAccount", "view");

  const showOperations =
    has("addProject", "view") ||
    has("transmittal", "view") ||
    has("checklist", "view") ||
    has("architect", "constructionReport");

  const showHR =
    has("timesheet", "view") ||
    has("employeeLog", "view") ||
    has("employeeLog", "viewAll") ||
    has("employeeReview", "view") ||
    has("employeeReview", "create") ||
    has("leaveApplication", "view") ||
    has("manageEmployees", "view") ||
    has("rolePermissions", "view") ||
    showCreateMailAccount;
  const showHiring =
    has("hiringDashboard", "view") ||
    has("jobOpenings", "view") ||
    has("candidates", "view") ||
    has("interviews", "view") ||
    has("offers", "view") ||
    has("joining", "view") ||
    has("closedHiring", "view") ||
    has("recruitmentReports", "view");
  const showSales =
    has("salesDashboard", "view") ||
    has("serviceMaster", "view") ||
    has("clientMaster", "view") ||
    has("bankMaster", "view") ||
    has("termsMaster", "view") ||
    has("quotations", "view") ||
    has("proformaInvoices", "view") ||
    has("invoices", "view");

  const showGST = has("gstReports", "view");
  const showTDS = has("tdsReceivable", "view") || has("tdsPayment", "view");
  const showBookKeeping =
    has("financeEntry", "view") ||
    has("financeDashboard", "view") ||
    showGST ||
    showTDS;

  const showFinance =
    has("financeDashboard", "view") ||
    has("projectCost", "view") ||
    has("financeEntry", "view") ||
    has("companyExpenses", "view") ||
    showBookKeeping ||
    showSales;

  const showAdmin =
    has("assets", "view") ||
    has("softwareEmployee", "view") ||
    has("hardwareEmployee", "view") ||
    has("softwareAllStaff", "view") ||
    has("hardwareAllStaff", "view");

  const showCRM =
    has("crmDashboard", "view") ||
    has("leads", "view") ||
    has("proposals", "view") ||
    hasSentEmailsAccess ||
    has("wonLeads", "view") ||
    has("lostLeads", "view") ||
    has("crmReports", "view");

  const Arrow = ({ open }) => (
    <span className={`sidebar-arrow ${open ? "open" : ""}`}>▼</span>
  );

  const SubLink = ({ to, icon, children }) => (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          isActive ? "sidebar-sub-link active" : "sidebar-sub-link"
        }
        onClick={closeMobile}
      >
        <span className="sidebar-sub-icon">{icon}</span>
        <span>{children}</span>
      </NavLink>
    </li>
  );

  return (
    <>
      {isMobile && open && (
        <div className="sidebar-overlay" onClick={() => setOpen(false)} />
      )}

      {isMobile && !open && (
        <button className="sidebar-toggle" onClick={() => setOpen(true)}>
          ☰
        </button>
      )}

      <aside className={`erp-sidebar ${isMobile && open ? "show" : ""}`}>
        {isMobile && open && (
          <div className="sidebar-mobile-head">
            <h3>Menu</h3>
            <button onClick={() => setOpen(false)}>✕</button>
          </div>
        )}

        {!isMobile && <h3 className="sidebar-title">Employee Menu</h3>}

        <ul className="sidebar-list">
          {!isClient && has("dashboard", "view") && (
            <li>
              <NavLink
                to="/"
                className={({ isActive }) =>
                  isActive ? "sidebar-main-link active" : "sidebar-main-link"
                }
                onClick={closeMobile}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">🏠</span>
                  <span>Dashboard</span>
                </span>
              </NavLink>
            </li>
          )}
          {/* ===================== TOOLS ===================== */}
          {!isClient && showTools && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setToolsOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">🛠️</span>
                  <span>Tool</span>
                </span>

                <Arrow open={toolsOpen} />
              </div>

              {toolsOpen && (
                <ul className="sidebar-sub-menu">
                  {has("notes", "view") && (
                    <SubLink to="/tools/notes" icon="📝">
                      Notes
                    </SubLink>
                  )}
                </ul>
              )}
            </li>
          )}
          {!isClient && showMessenger && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setMessengerOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">💬</span>
                  <span>Messenger</span>
                </span>

                <Arrow open={messengerOpen} />
              </div>

              {messengerOpen && (
                <ul className="sidebar-sub-menu">
                  {has("messengerUser", "view") && (
                    <SubLink to="/messenger" icon="👤">
                      User wise
                    </SubLink>
                  )}

                  {has("messengerProject", "view") && (
                    <SubLink to="/messenger/project-communication" icon="📁">
                      Project-wise
                    </SubLink>
                  )}

                  {has("messengerCategory", "view") && (
                    <SubLink to="/messenger/category-communication" icon="🏷️">
                      Category wise
                    </SubLink>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* ===================== EMAIL (permission: email) ===================== */}
          {!isClient && showEmail && (
            <li>
              <NavLink
                to="/email/inbox"
                className={() =>
                  location.pathname === "/email" ||
                  location.pathname.startsWith("/email/")
                    ? "sidebar-main-link active"
                    : "sidebar-main-link"
                }
                onClick={closeMobile}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">✉️</span>
                  <span>Email</span>
                </span>
              </NavLink>
            </li>
          )}

          {!isClient && showOperations && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setOperationsOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">⚙️</span>
                  <span>Operations</span>
                </span>
                <Arrow open={operationsOpen} />
              </div>

              {operationsOpen && (
                <ul className="sidebar-sub-menu">
                  <SubLink to="/operations-dashboard" icon="📊">
                    Dashboard
                  </SubLink>

                  {has("addProject", "view") && (
                    <SubLink to="/projects/new" icon="📂">
                      Add Project
                    </SubLink>
                  )}

                  {has("transmittal", "view") && (
                    <SubLink to="/transmittal" icon="📤">
                      Transmittal
                    </SubLink>
                  )}

                  {has("checklist", "view") && (
                    <SubLink to="/checklist" icon="✅">
                      Checklist
                    </SubLink>
                  )}
                  {has("architect", "constructionReport") && (
                    <li>
                      <div
                        className="sidebar-sub-parent"
                        onClick={() => setArchitectOpen((v) => !v)}
                      >
                        <span>
                          <span className="sidebar-sub-icon">🏛️</span>
                          Architect
                        </span>

                        <Arrow open={architectOpen} />
                      </div>

                      {architectOpen && (
                        <ul className="sidebar-inner-menu">
                          <SubLink to="/construction-report" icon="🏗️">
                            Construction Report
                          </SubLink>
                        </ul>
                      )}
                      <li>
                        <div
                          className="sidebar-sub-parent"
                          onClick={() => setProjectManagementOpen((v) => !v)}
                        >
                          <span>
                            <span className="sidebar-sub-icon">📁</span>
                            Project Management
                          </span>

                          <Arrow open={projectManagementOpen} />
                        </div>
                      </li>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* CRM */}
          {!isClient && showCRM && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setCrmOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">📈</span>
                  <span> Business Development</span>
                </span>
                <Arrow open={crmOpen} />
              </div>

              {crmOpen && (
                <ul className="sidebar-sub-menu">
                  {has("crmDashboard", "view") && (
                    <SubLink to="/crm-dashboard" icon="📊">
                      CRM Dashboard
                    </SubLink>
                  )}

                  {has("leads", "view") && (
                    <SubLink to="/leads" icon="🗂️">
                      Leads
                    </SubLink>
                  )}

                  {has("proposals", "view") && (
                    <SubLink to="/proposals" icon="🔍">
                      Proposals
                    </SubLink>
                  )}

                  {hasSentEmailsAccess && (
                    <li>
                      <div
                        className="sidebar-sub-parent"
                        onClick={() => {
                          setSentEmailsOpen((v) => !v);
                          if (!location.pathname.startsWith("/sent-emails")) {
                            navigate("/sent-emails/intro");
                            closeMobile();
                          }
                        }}
                      >
                        <span>
                          <span className="sidebar-sub-icon">📧</span>
                          Sent Emails
                        </span>
                        <Arrow open={sentEmailsOpen} />
                      </div>

                      {sentEmailsOpen && (
                        <ul className="sidebar-inner-menu">
                          {has("sentEmails", "viewIntro") && (
                            <SubLink to="/sent-emails/intro" icon="📩">
                              Intro Mail
                            </SubLink>
                          )}
                          {has("sentEmails", "viewPortfolio") && (
                            <SubLink to="/sent-emails/portfolio" icon="📁">
                              Portfolio Mail
                            </SubLink>
                          )}
                          {has("sentEmails", "viewProposal") && (
                            <SubLink to="/sent-emails/proposal" icon="📄">
                              Proposal Mail
                            </SubLink>
                          )}
                        </ul>
                      )}
                    </li>
                  )}

                  {has("wonLeads", "view") && (
                    <SubLink to="/wons-leads" icon="📞">
                      Won Leads
                    </SubLink>
                  )}

                  {has("lostLeads", "view") && (
                    <SubLink to="/lost-leads" icon="🤝">
                      Lost Leads
                    </SubLink>
                  )}

                  {has("crmReports", "view") && (
                    <SubLink to="/reports" icon="📈">
                      Reports
                    </SubLink>
                  )}
                </ul>
              )}
            </li>
          )}

          {!isClient && showHR && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setHrOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">👥</span>
                  <span>HR</span>
                </span>
                <Arrow open={hrOpen} />
              </div>

              {hrOpen && (
                <ul className="sidebar-sub-menu">
                  {/* Create Mail Account (permission: createMailAccount) */}
                  {showCreateMailAccount && (
                    <SubLink to="/hr/create-mail-account" icon="📬">
                      Create Mail Account
                    </SubLink>
                  )}
                  {has("timesheet", "view") && (
                    <SubLink to="/timesheet" icon="⏱️">
                      Timesheet
                    </SubLink>
                  )}

                  {has("employeeLog", "view") && (
                    <SubLink to={`/employee/logs/${user?._id}`} icon="📝">
                      Employee Log Detail
                    </SubLink>
                  )}

                  {has("employeeLog", "viewAll") && (
                    <SubLink to="/admin/log-summary" icon="📄">
                      All Employee Logs
                    </SubLink>
                  )}

                  {has("leaveApplication", "view") && (
                    <SubLink to="/leave-application" icon="🏖️">
                      Leave Application
                    </SubLink>
                  )}
                  {has("leaveApplication", "approve") && (
                    <SubLink to="/leave-management" icon="✅">
                      Leave Management
                    </SubLink>
                  )}
                  {has("manageEmployees", "view") && (
                    <SubLink to="/manage-employees" icon="👥">
                      Manage Employee
                    </SubLink>
                  )}

                  {has("employeeReview", "view") && (
                    <SubLink to="/employee-reviews" icon="⭐">
                      Employee Review
                    </SubLink>
                  )}

                  {has("employeeReview", "create") && (
                    <SubLink to="/admin-employee-review-detail" icon="⭐">
                      Create Employee Review
                    </SubLink>
                  )}
                  {has("projectHoursSummary", "view") && (
                    <SubLink to="/project-hours-summary" icon="📊">
                      Project-wise Hours Summary
                    </SubLink>
                  )}
                  {has("rolePermissions", "view") && (
                    <SubLink to="/role-permissions" icon="🔐">
                      Role &amp; Permission
                    </SubLink>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Hiring Management - separate main menu */}
          {!isClient && showHiring && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setHiringOpen((value) => !value)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">🧑‍💼</span>
                  <span>Hiring Management</span>
                </span>

                <Arrow open={hiringOpen} />
              </div>

              {hiringOpen && (
                <ul className="sidebar-sub-menu">
                  {has("hiringDashboard", "view") && (
                    <SubLink to="/hr-dashboard" icon="📊">
                      Hiring Dashboard
                    </SubLink>
                  )}

                  {has("jobOpenings", "view") && (
                    <SubLink to="/hr/job-openings" icon="💼">
                      Job Openings
                    </SubLink>
                  )}

                  {has("candidates", "view") && (
                    <SubLink to="/hr/candidates" icon="👤">
                      Candidates
                    </SubLink>
                  )}

                  {has("interviews", "view") && (
                    <SubLink to="/hr/interviews" icon="🗓️">
                      Interviews
                    </SubLink>
                  )}

                  {has("offers", "view") && (
                    <SubLink to="/hr/offers" icon="📄">
                      Offers
                    </SubLink>
                  )}

                  {has("joining", "view") && (
                    <SubLink to="/hr/joining" icon="🤝">
                      Upcoming Joinings
                    </SubLink>
                  )}

                  {has("closedHiring", "view") && (
                    <SubLink to="/hr/closed-hiring" icon="✅">
                      Closed Hiring
                    </SubLink>
                  )}

                  {has("recruitmentReports", "view") && (
                    <SubLink to="/hr/recruitment-reports" icon="📈">
                      Recruitment Reports
                    </SubLink>
                  )}
                </ul>
              )}
            </li>
          )}

          {!isClient && showFinance && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setFinanceOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">💰</span>
                  <span>Finance</span>
                </span>
                <Arrow open={financeOpen} />
              </div>

              {financeOpen && (
                <ul className="sidebar-sub-menu">
                  {has("financeDashboard", "view") && (
                    <SubLink to="/company-expenses/svb" icon="📈">
                      Finance Dashboard
                    </SubLink>
                  )}

                  {has("projectCost", "view") && (
                    <SubLink to="/project-cost-report" icon="💵">
                      Project Cost
                    </SubLink>
                  )}

                  {/* Book Keeping: preserve existing sidebar classes and styling */}
                  {showBookKeeping && (
                    <li>
                      <div
                        className="sidebar-sub-parent"
                        onClick={() => setBookKeepingOpen((v) => !v)}
                      >
                        <span>
                          <span className="sidebar-sub-icon">📂</span>
                          Book Keeping
                        </span>
                        <Arrow open={bookKeepingOpen} />
                      </div>

                      {bookKeepingOpen && (
                        <ul
                          className="sidebar-inner-menu"
                          style={{ margin: 0, padding: 0, borderLeft: "none" }}
                        >
                          {(has("financeEntry", "view") ||
                            has("financeDashboard", "view")) && (
                            <SubLink to="/finance-entry" icon="💳">
                              Finance Entry
                            </SubLink>
                          )}

                          {(showGST || showTDS) && (
                            <li>
                              <div
                                className="sidebar-sub-parent"
                                onClick={() => setFinanceReportsOpen((v) => !v)}
                              >
                                <span>
                                  <span className="sidebar-sub-icon">📊</span>
                                  Reports
                                </span>
                                <Arrow open={financeReportsOpen} />
                              </div>

                              {financeReportsOpen && (
                                <ul
                                  className="sidebar-inner-menu"
                                  style={{
                                    margin: 0,
                                    padding: 0,
                                    borderLeft: "none",
                                  }}
                                >
                                  {showGST && (
                                    <li>
                                      <div
                                        className="sidebar-sub-parent"
                                        onClick={() =>
                                          setGstReportsOpen((v) => !v)
                                        }
                                      >
                                        <span>
                                          <span className="sidebar-sub-icon">
                                            🧾
                                          </span>
                                          GST
                                        </span>
                                        <Arrow open={gstReportsOpen} />
                                      </div>
                                      {gstReportsOpen && (
                                        <ul
                                          className="sidebar-inner-menu"
                                          style={{
                                            margin: 0,
                                            padding: 0,
                                            borderLeft: "none",
                                          }}
                                        >
                                          <SubLink
                                            to="/finance/reports/gst/r1-sales"
                                            icon="📄"
                                          >
                                            R1 Sales
                                          </SubLink>
                                          <SubLink
                                            to="/finance/reports/gst/3b-purchase"
                                            icon="📄"
                                          >
                                            3B Purchase
                                          </SubLink>
                                        </ul>
                                      )}
                                    </li>
                                  )}

                                  {showTDS && (
                                    <li>
                                      <div
                                        className="sidebar-sub-parent"
                                        onClick={() =>
                                          setTdsReportsOpen((v) => !v)
                                        }
                                      >
                                        <span>
                                          <span className="sidebar-sub-icon">
                                            📁
                                          </span>
                                          TDS
                                        </span>
                                        <Arrow open={tdsReportsOpen} />
                                      </div>
                                      {tdsReportsOpen && (
                                        <ul
                                          className="sidebar-inner-menu"
                                          style={{
                                            margin: 0,
                                            padding: 0,
                                            borderLeft: "none",
                                          }}
                                        >
                                          {has("tdsReceivable", "view") && (
                                            <SubLink
                                              to="/finance/reports/tds/receivable"
                                              icon="📥"
                                            >
                                              TDS Receivable
                                            </SubLink>
                                          )}
                                          {has("tdsPayment", "view") && (
                                            <SubLink
                                              to="/finance/reports/tds/payable"
                                              icon="💳"
                                            >
                                              TDS Payment
                                            </SubLink>
                                          )}
                                        </ul>
                                      )}
                                    </li>
                                  )}
                                </ul>
                              )}
                            </li>
                          )}
                        </ul>
                      )}
                    </li>
                  )}

                  {showSales && (
                    <li>
                      <div
                        className="sidebar-sub-parent"
                        onClick={() => setSalesOpen((v) => !v)}
                      >
                        <span>
                          <span className="sidebar-sub-icon">💼</span>
                          Sales
                        </span>
                        <Arrow open={salesOpen} />
                      </div>

                      {salesOpen && (
                        <ul className="sidebar-inner-menu">
                          {has("salesDashboard", "view") && (
                            <SubLink to="/sales/dashboard" icon="📊">
                              Sales Dashboard
                            </SubLink>
                          )}

                          {(has("serviceMaster", "view") ||
                            has("clientMaster", "view") ||
                            has("bankMaster", "view") ||
                            has("termsMaster", "view")) && (
                            <li>
                              <div
                                className="sidebar-sub-parent"
                                onClick={() => setSalesMastersOpen((v) => !v)}
                              >
                                <span>
                                  <span className="sidebar-sub-icon">📁</span>
                                  Masters
                                </span>
                                <Arrow open={salesMastersOpen} />
                              </div>

                              {salesMastersOpen && (
                                <ul className="sidebar-inner-menu">
                                  {has("serviceMaster", "view") && (
                                    <SubLink to="/sales/services" icon="🛠️">
                                      Service Master
                                    </SubLink>
                                  )}

                                  {has("clientMaster", "view") && (
                                    <SubLink to="/sales/clients" icon="👥">
                                      Client Master
                                    </SubLink>
                                  )}

                                  {has("bankMaster", "view") && (
                                    <SubLink to="/sales/banks" icon="🏦">
                                      Bank Detail Master
                                    </SubLink>
                                  )}

                                  {has("termsMaster", "view") && (
                                    <SubLink to="/sales/terms" icon="📋">
                                      Terms & Conditions
                                    </SubLink>
                                  )}
                                </ul>
                              )}
                            </li>
                          )}

                          {has("quotations", "view") && (
                            <SubLink to="/sales/quotations" icon="📄">
                              Quotations
                            </SubLink>
                          )}

                          {has("proformaInvoices", "view") && (
                            <SubLink to="/sales/proforma-invoices" icon="📃">
                              Proforma Invoices
                            </SubLink>
                          )}

                          {has("invoices", "view") && (
                            <SubLink to="/sales/invoices" icon="🧾">
                              Invoices
                            </SubLink>
                          )}
                        </ul>
                      )}
                    </li>
                  )}

                  {has("companyExpenses", "view") && (
                    <li>
                      <div
                        className="sidebar-sub-parent"
                        onClick={() => setCompanyExpenseOpen((v) => !v)}
                      >
                        <span>
                          <span className="sidebar-sub-icon">📊</span>
                          Company Expenses
                        </span>
                        <Arrow open={companyExpenseOpen} />
                      </div>

                      {companyExpenseOpen && (
                        <ul className="sidebar-inner-menu">
                          <SubLink to="/company-expenses/svb" icon="🏢">
                            SVB
                          </SubLink>
                          <SubLink to="/company-expenses/sdc" icon="🏢">
                            SDC
                          </SubLink>
                          <SubLink to="/company-expenses/magnira" icon="🏢">
                            Magnira
                          </SubLink>
                        </ul>
                      )}
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
          {!isClient && showAdmin && (
            <li>
              <div
                className="sidebar-group"
                onClick={() => setAdminOpen((v) => !v)}
              >
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">🏢</span>
                  <span>Admin & Internal Management</span>
                </span>
                <Arrow open={adminOpen} />
              </div>

              {adminOpen && (
                <ul className="sidebar-sub-menu">
                  <li className="sidebar-section-label">Asset</li>

                  {has("softwareEmployee", "view") && (
                    <SubLink to="/software-employee" icon="💻">
                      My Software
                    </SubLink>
                  )}
                  {has("hardwareEmployee", "view") && (
                    <SubLink to="/hardware-employee" icon="🖥️">
                      My Hardware
                    </SubLink>
                  )}
                  {has("softwareAllStaff", "view") && (
                    <SubLink to="/software" icon="📦">
                      Software All Staff
                    </SubLink>
                  )}
                  {has("hardwareAllStaff", "view") && (
                    <SubLink to="/hardware" icon="🗄️">
                      Hardware All Staff
                    </SubLink>
                  )}
                </ul>
              )}
            </li>
          )}

          {isITManager && (
            <li>
              <Link to="/active-sessions" className="sidebar-main-link">
                <span className="sidebar-menu-left">
                  <span className="sidebar-icon">🔐</span>
                  <span>Active Sessions</span>
                </span>
              </Link>
            </li>
          )}
        </ul>
      </aside>
    </>
  );
};

export default Sidebar;
